// ============================================================================
// POs / Shipments tabs — the agreed layout (brief §4.3), with "–" in place of every number until
// their live data and rules are built (O15). Same sections, labels and columns as before, so the
// page is ready to fill in. Stage rows come from config/stages.ts.
// ============================================================================
import { Segmented, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useState } from "react";
import styles from "../index.module.scss";

const DASH = <span className={styles.metricZero}>–</span>;
const dashCol = (title: string, first = false): ColumnsType<Record<string, never>>[number] => ({
  title,
  key: title,
  align: first ? "left" : "right",
  render: () => DASH,
});

const Section: React.FC<{ title: string; note?: string; children: React.ReactNode }> = ({ title, note, children }) => (
  <section className={styles.panel}>
    <div className={styles.panelHead}>
      <h3 className={styles.panelTitle}>{title}</h3>
    </div>
    {note ? <div className={styles.sectionNote}>{note}</div> : null}
    {children}
  </section>
);

const Cell: React.FC<{ label: string; sub?: string }> = ({ label, sub }) => (
  <div className={styles.stripCell}>
    <div className={styles.stripLabel}>{label}</div>
    <div className={styles.stripValue}>{DASH}</div>
    <div className={styles.stripSub}>{sub ?? " "}</div>
  </div>
);

// An empty table that still shows its columns, with a single dash row.
export const DashTable: React.FC<{ columns: string[]; rows?: { label: string; owner?: string }[] }> = ({ columns, rows }) => {
  const cols: ColumnsType<{ key: string; label: string; owner?: string }> = columns.map((c, i) =>
    i === 0 && rows
      ? { title: c, dataIndex: "label", render: (v: string) => <span className={styles.strong}>{v}</span> }
      : i === 1 && rows && rows.some((r) => r.owner)
      ? { title: c, dataIndex: "owner", render: (v: string) => <span className={styles.muted}>{v}</span> }
      : ({ ...dashCol(c, i === 0), width: i === 0 ? undefined : 100 } as any),
  );
  return (
    <Table
      size="small"
      rowKey="key"
      columns={cols}
      dataSource={rows ? rows.map((r) => ({ key: r.label, ...r })) : [{ key: "dash", label: "" }]}
      pagination={false}
      className={styles.denseTable}
      scroll={{ x: 900 }}
    />
  );
};

// Billing (POs tab): structure only until the billing source is built (Q5).
export const BillingStructure: React.FC = () => {
  const [billingRange, setBillingRange] = useState("last_7_days");
  return (
    <Section title="Billing" note="Structure only: billing conditions are wired later.">
      <div className={styles.sectionToolbar}>
        <Segmented
          size="small"
          value={billingRange}
          onChange={(v) => setBillingRange(String(v))}
          options={[
            { label: "Last 7 days", value: "last_7_days" },
            { label: "Today", value: "today" },
            { label: "Yesterday", value: "yesterday" },
          ]}
        />
      </div>
      <div className={styles.strip}>
        <Cell label="Received (GRN)" sub="SLA from GRN" />
        <Cell label="Billed within SLA" />
        <Cell label="Billed late" />
        <Cell label="Not billed · within SLA" />
        <Cell label="Not billed · past SLA" />
        <Cell label="Avg time to bill" />
      </div>
    </Section>
  );
};

export const PO_OWNER_COLUMNS = [
  "PO owner", "POs", "Weight", "Received %", "On time", "Major", "Minor", "Breached", "Waiting for PO", "Unbilled past SLA", "Open esc.",
];
